import { useState } from 'react';
import first from 'lodash.first';
import { node, bool, arrayOf, shape, string } from 'prop-types';

import CountryFlag from 'components/base/CountryFlag';
import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';

import { country as countryPropType } from 'propTypes';
import metricPropType from 'propTypes/metric';

import {
  TableRow as StyledTableRow,
  PositionCell,
  CountryCell,
  ExpandCell,
  AlignContent,
  StyledCaret,
} from '../shared/TerritoryTable.style';

/**
 * (Territory) TableRow formats and renders the data of a country in a row.
 *
 * @param  {Object} props.country performance and meta data of a country
 * @param  {Object} props.territory the territory for the current row
 * @param  {Object} props.sortMetric metric to sort by
 * @param  {Object} props.children renders `ExpandableTableRow` when passed
 * @param  {Boolean} props.isLargeScreen true if screen matches min-width
 * @param  {Boolean} props.isStatic if true this row is not be affected by sorting
 *                                  and is styled differently
 */
function TableRow(props) {
  const {
    tableHeaders,
    territory,
    sortMetric,
    children,
    isLargeScreen,
    isStatic,
    isStreamingPage,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const entityData = first(territory.data);
  const allDataCellsOrOnlyActive = tableHeaders
    .filter(filterDataCells)
    .filter(filterByBreakpoint);

  // Country name is not a data cell we want to loop over.
  function filterDataCells(header) {
    const ignore = new Set(['countryName', 'expand', 'countryRank']);
    return !ignore.has(header.value);
  }

  // On large screens, filter nothing, otherwise only return
  // the active metric as mobile is single column.
  function filterByBreakpoint(header) {
    const keys = [header.key, header.change];
    const isActive = keys.filter(Boolean).includes(sortMetric.key);
    return isLargeScreen || isActive;
  }

  function toggleExpand() {
    if (children) {
      setIsExpanded(!isExpanded);
    }
  }

  return (
    <>
      <StyledTableRow
        onClick={toggleExpand}
        withExpandableRows={Boolean(children)}
        isStatic={isStatic}
      >
        <PositionCell>{entityData.countryRank}</PositionCell>

        <CountryCell>
          <AlignContent alignLeft>
            {
              <CountryFlag
                id={territory.id || territory.countryId}
                displayName={territory.countryName}
                flagImgSmall={territory.flagImgSmall}
                flagImgMedium={territory.flagImgMedium}
                flagImgLarge={territory.flagImgLarge}
                small
              />
            }
            {territory.countryName}
          </AlignContent>
        </CountryCell>

        {allDataCellsOrOnlyActive.map((header, index) => (
          <DoubleMetricCell
            key={Object.values(header).join('-')}
            sortBy={sortMetric}
            header={header}
            entityData={entityData}
            paddingRight={isStreamingPage ? '2em' : '1em'}
            isCentered={allDataCellsOrOnlyActive.length - 1 !== index}
            tableName={isStreamingPage ? 'streamingTerritories' : undefined}
          />
        ))}

        {children && (
          <ExpandCell>
            <AlignContent center>
              <StyledCaret open={isExpanded} />
            </AlignContent>
          </ExpandCell>
        )}
      </StyledTableRow>

      {isExpanded && children}
    </>
  );
}

TableRow.propTypes = {
  tableHeaders: arrayOf(
    shape({
      change: string,
      deprecatedKey: string,
      key: string,
      label: string.isRequired,
      range: string,
      type: string,
      value: string.isRequired,
    }).isRequired
  ).isRequired,
  territory: countryPropType.isRequired,
  sortMetric: metricPropType.isRequired,
  children: node,
  isLargeScreen: bool.isRequired,
  isStatic: bool.isRequired,
  isStreamingPage: bool,
};

export default TableRow;
